import { useCallback } from "react";
import styled from "@emotion/styled";
import { uiIcons } from "../../assets/icons";
import ModalBase, { ModalType } from "../modals/ModalBase";
import { useDownload } from "@atlas-int/viz";
import { googleAnalyticsCustomEvent } from "../../sharedUtilities/analytics";
import {spacing, borderRadius, surface} from "@atlas-int/tokens";
import { NavModalTrigger, Button } from "@atlas-int/ui";

const DownloadButtonsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: ${spacing[10]};
  width: 100%;
  margin-bottom: ${spacing[8]};
`;

const DownloadOptionButton = styled(Button)`
  flex-direction: column;
  padding: ${spacing[12]};
  border-radius: ${borderRadius.lg};

  &:hover:not(:disabled) {
    background-color: ${surface.muted};
  }
`;

const DownloadOptionIcon = styled.img`
  width: ${spacing[20]};
  height: ${spacing[20]};
  margin-bottom: ${spacing[4]};
`;

function sanitizeFilename(name: string): string {
  return name
    .trim()
    .split(/[^a-zA-Z0-9]+/)
    .filter(Boolean)
    .join("-");
}

/**
 * Collect @font-face rules from stylesheets and inline font URLs as base64
 * data URIs so html2canvas can render custom fonts inside SVG foreignObject.
 */
async function embedFonts(doc: Document): Promise<void> {
  const fontRules: CSSFontFaceRule[] = [];
  for (const sheet of Array.from(document.styleSheets)) {
    try {
      for (const rule of Array.from(sheet.cssRules)) {
        if (rule instanceof CSSFontFaceRule) {
          fontRules.push(rule);
        }
      }
    } catch {
      // cross-origin sheets throw — skip
    }
  }
  if (fontRules.length === 0) return;

  const cssTexts = await Promise.all(
    fontRules.map(async (rule) => {
      let css = rule.cssText;
      const urlMatches = css.match(/url\(["']?([^"')]+)["']?\)/g);
      if (!urlMatches) return css;

      for (const urlMatch of urlMatches) {
        const raw = urlMatch.replace(/url\(["']?/, "").replace(/["']?\)/, "");
        if (raw.startsWith("data:")) continue;
        try {
          const resp = await fetch(raw);
          const blob = await resp.blob();
          const dataUrl = await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.readAsDataURL(blob);
          });
          css = css.replace(raw, dataUrl);
        } catch {
          // skip fonts that can't be fetched
        }
      }
      return css;
    }),
  );

  const style = doc.createElement("style");
  style.textContent = cssTexts.join("\n");
  doc.head.appendChild(style);
}

const DownloadModalButton = ({
  menuOptionClassName,
  isNavigationBarOpen,
}: any) => {
  const { dataRef, containerRef, titleRef } = useDownload();

  const handleImageDownload = useCallback(async () => {
    const titleString = sanitizeFilename(titleRef.current ?? "");
    const container = containerRef.current;
    if (!container) return;

    const [{ default: html2canvas }, { saveAs }] = await Promise.all([
      import("html2canvas"),
      import("file-saver"),
    ]);

    html2canvas(container, {
      backgroundColor: "#ffffff",
      scale: 2,
      useCORS: true,
      logging: false,
      onclone: async (clonedDoc, clonedEl) => {
        // Measure controls height before removing so we can shrink the container
        const controlsEl = clonedEl.querySelector("[data-download-exclude]");
        const controlsHeight = controlsEl ? controlsEl.getBoundingClientRect().height : 0;

        clonedEl
          .querySelectorAll("[data-download-exclude]")
          .forEach((el) => el.remove());
        clonedEl
          .querySelectorAll<HTMLElement>("[data-download-only]")
          .forEach((el) => { el.style.opacity = "1"; });

        // Remove controls row from the grid and shrink container to match
        clonedEl.style.gridTemplateRows = "0 auto minmax(0,1fr)";
        const currentHeight = clonedEl.getBoundingClientRect().height;
        clonedEl.style.height = `${currentHeight - controlsHeight}px`;

        await embedFonts(clonedDoc);
      },
    })
      .then((canvas) => {
        canvas.toBlob((blob) => {
          if (blob) {
            saveAs(blob, `${titleString}.png`);
          }
        });
      })
      .catch((err) => {
        console.error("Image download failed", err);
      });
  }, [titleRef, containerRef]);

  const handleDataDownload = useCallback(async () => {
    const titleString = sanitizeFilename(titleRef.current ?? "");

    if (dataRef.current && dataRef.current.length > 0) {
      const { saveAs } = await import("file-saver");
      const headers = Object.keys(dataRef.current[0]);
      const csvContent = [
        headers.map((header) => JSON.stringify(header)).join(","),
        ...dataRef.current.map((row) =>
          headers
            .map((header) => {
              if (typeof row[header] === "number") {
                return row[header];
              } else {
                return JSON.stringify(row[header]);
              }
            })
            .join(","),
        ),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      saveAs(blob, `${titleString}.csv`);
    } else {
      console.error("No data available for download");
    }
  }, [dataRef, titleRef]);

  return (
    <NavModalTrigger
      icon={uiIcons.download}
      iconAlt="Download"
      label="Download"
      isNavOpen={isNavigationBarOpen}
      menuOptionClassName={menuOptionClassName}
      onOpen={() =>
        googleAnalyticsCustomEvent("leftnav", "click", "download", "explore")
      }
    >
      {({ isOpen, onToggle }) =>
        isOpen ? (
          <ModalBase modalType={ModalType.Generic} toggleModal={onToggle}>
            <DownloadButtonsContainer>
              <DownloadOptionButton variant="default" size="lg" onClick={handleImageDownload}>
                <DownloadOptionIcon src={uiIcons.imageDownload} alt="Download Image" />
                Download Image
              </DownloadOptionButton>
              <DownloadOptionButton variant="default" size="lg" onClick={handleDataDownload}>
                <DownloadOptionIcon src={uiIcons.dataDownload} alt="Download Data" />
                Download Data
              </DownloadOptionButton>
            </DownloadButtonsContainer>
          </ModalBase>
        ) : null
      }
    </NavModalTrigger>
  );
};

export default DownloadModalButton;
